import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

import CodeViewer from "@/components/CodeViewer";

// Fenced/multi-line code → Shiki (theme-aware, highlighted); inline code stays a plain <code>
// (styled by the surrounding prose). `pre` is flattened so the CodeViewer isn't double-wrapped.
const MD_COMPONENTS: Components = {
  pre: ({ children }) => <>{children}</>,
  code({ className, children }) {
    const text = String(children ?? "");
    const lang = /language-(\w+)/.exec(className || "")?.[1];
    if (lang || text.includes("\n")) {
      return <CodeViewer code={text.replace(/\n$/, "")} lang={lang} />;
    }
    return <code className={className}>{children}</code>;
  },
};

function splitFrontMatter(md: string): { frontMatter: string | null; body: string } {
  const m = md.match(/^(---\s*\n[\s\S]*?\n---)\s*\n?/);
  return m ? { frontMatter: m[1], body: md.slice(m[0].length) } : { frontMatter: null, body: md };
}

// PROSE: adapts via dark:prose-invert (requires darkMode selector in tailwind.config)
const PROSE =
  "prose dark:prose-invert max-w-none prose-pre:bg-black/40 prose-a:text-accent " +
  "prose-headings:text-text prose-p:text-text/80 prose-li:text-text/80 prose-strong:text-text";

// DOC: renders inside a dark bg-[#0d1117] container — always use fixed slate colors
// so text is always light regardless of which theme the page is in.
const DOC =
  "prose prose-invert max-w-none font-mono " +
  "prose-headings:font-medium prose-headings:text-slate-100 " +
  "prose-h1:mt-0 prose-h1:mb-6 prose-h1:text-[18px] " +
  "prose-h2:mt-8 prose-h2:mb-3 prose-h2:text-[15px] " +
  "prose-h3:mt-6 prose-h3:mb-2 prose-h3:text-[14px] " +
  "prose-p:my-3 prose-p:text-[14px] prose-p:leading-relaxed prose-p:text-slate-300 " +
  "prose-ul:my-3 prose-ol:my-3 prose-li:my-1.5 prose-li:text-[14px] prose-li:text-slate-300 prose-li:marker:text-slate-500 " +
  "prose-a:text-slate-200 prose-a:underline " +
  "prose-strong:text-slate-100 prose-strong:font-semibold " +
  "prose-code:text-slate-200 prose-code:font-normal prose-code:before:content-[''] prose-code:after:content-[''] " +
  "prose-pre:rounded-lg prose-pre:border prose-pre:border-white/10 prose-pre:bg-[#161b22] prose-pre:p-4 prose-pre:text-[13px] " +
  "prose-blockquote:border-l-primary/50 prose-blockquote:text-slate-400 " +
  "prose-table:text-[13px] prose-th:text-slate-100 prose-td:text-slate-300 prose-hr:border-white/10";

export default function SkillMarkdown({
  md,
  variant = "prose",
  showFrontMatter = false,
}: {
  md: string;
  variant?: "prose" | "doc";
  showFrontMatter?: boolean;
}) {
  const { frontMatter, body } = splitFrontMatter(md);
  return (
    <div className={variant === "doc" ? DOC : PROSE}>
      {showFrontMatter && frontMatter && (
        // sp-frontmatter: front-matter is prose (a long `description:`), not code, so it wraps
        // instead of forcing a sideways scroll to finish the sentence. See index.css.
        <div className="sp-frontmatter mb-5">
          <CodeViewer code={frontMatter} lang="yaml" />
        </div>
      )}
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{body}</ReactMarkdown>
    </div>
  );
}
